import * as React from 'react';
import { ChangeEventHandler, useCallback } from 'react';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import cn from 'classnames';

import Checkbox from '../../../BaseComponents/Checkbox';
import { useTheme } from '../../../theme';
import { useToggleable } from '../../../hooks';
import { SubsidyType } from '@websky/graphql';
import { useTranslation } from 'react-i18next';
import CdnImage from '../../../CdnImage';

export interface Props {
	onChange: React.Dispatch<React.ChangeEvent>;
	containerClassName?: string;
	disabled?: boolean;
	id: string;
	type: SubsidyType;
	className?: string;
	isSelected?: boolean;
	content?: (
		props: Pick<Props, 'disabled' | 'type'> & {
			onChange: ChangeEventHandler<HTMLInputElement>;
			isActive: boolean;
			icon: React.ReactElement;
			title: string;
		}
	) => React.ReactElement;
}
const SubsidiesSelector: React.FC<Props> = props => {
	const { SubsidiesSelector: theme } = useTheme('Subsidies');
	const { setOpen: setActive, isOpen: isActive } = useToggleable(false);
	const { t } = useTranslation('Subsidies');

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			props.onChange(e);
			setActive(e.target.checked);
		},
		[props.onChange]
	);

	const subsidyKey = `${props.type}${props.type === SubsidyType.Other ? `_${props.id}` : ''}`;

	const title = t(`program_${subsidyKey}`);
	const description = t(`description_${subsidyKey}`);

	return (
		<FormGroup
			className={cn(
				theme.container,
				{
					[theme.active]: props.isSelected,
					[theme.disabled]: props.disabled
				},
				props.className
			)}
		>
			{props.content?.({
				isActive,
				icon: <CdnImage path={`Subsidy/${subsidyKey}.svg`} />,
				disabled: props.disabled,
				type: props.type,
				title: title,
				onChange: handleChange
			}) ?? (
				<FormControlLabel
					disabled={props.disabled}
					className={theme.label}
					classes={{ label: theme.label }}
					control={<Checkbox className={theme.checkbox} checked={props.isSelected} onChange={handleChange} />}
					label={
						<div className={theme.subsidyInfo}>
							<span className={theme.text}>
								<div className={theme.title}>{title}</div>
								<div className={theme.description}>{description}</div>
							</span>
							<CdnImage path={`Subsidy/${subsidyKey}.svg`} />
						</div>
					}
				/>
			)}
		</FormGroup>
	);
};

export default SubsidiesSelector;
